import { useEffect, useState } from "react";
import DSDialog from "views/system-design/DSDialog";
import DSDialogTitle from "views/system-design/DSDialogTitle";
import DSDialogContent from "views/system-design/DSDialogContent";
import DSDialogActions from "views/system-design/DSDialogActions";
import DSButton from "views/system-design/DSButton";
import DSBox from "views/system-design/DSBox";
import DSTypography from "views/system-design/DSTypography";
import DSTextField from "views/system-design/DSTextField";
import DSEnumSelect from "views/system-design/DSEnumSelect";
import DSCircularProgress from "views/system-design/DSCircularProgress";
import { extractApiErrorMessage } from "../../../utils/util";
import {
  useAddClocksToFeatureBetaGroup,
  useCreateFeatureBetaGroup,
  useFeatureBetaGroups,
} from "../../FeatureBetaGroups/hooks/useFeatureBetaGroups";

const CREATE_NEW = "__create_new__";

type AddToBetaGroupDialogProps = {
  open: boolean;
  onClose: () => void;
  devicesIds: string[];
};

/**
 * Adds the current device selection to a feature beta group.
 *
 * Deliberately not a `DeviceCommand`: nothing is sent to the clocks here, the
 * ids are just copied into the group. Rolling the update out is a separate,
 * explicit action on the Feature Beta Groups page.
 */
const AddToBetaGroupDialog = ({
  open,
  onClose,
  devicesIds,
}: AddToBetaGroupDialogProps) => {
  const { data: groups, isLoading } = useFeatureBetaGroups();
  const { mutateAsync: addClocks, isPending: isAdding } =
    useAddClocksToFeatureBetaGroup();
  const { mutateAsync: createGroup, isPending: isCreating } =
    useCreateFeatureBetaGroup();

  const [groupId, setGroupId] = useState("");
  const [newName, setNewName] = useState("");
  const [newVersion, setNewVersion] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setGroupId("");
      setNewName("");
      setNewVersion("");
      setError(null);
      setResult(null);
    }
  }, [open]);

  const isNew = groupId === CREATE_NEW;
  const versionNumber = Number(newVersion);
  const isPending = isAdding || isCreating;
  const canSubmit =
    !isPending &&
    devicesIds.length > 0 &&
    (isNew
      ? newName.trim().length > 0 &&
        Number.isFinite(versionNumber) &&
        versionNumber > 0
      : !!groupId);

  const handleSubmit = async () => {
    setError(null);
    try {
      const targetId = isNew
        ? (
            await createGroup({
              name: newName.trim(),
              version: versionNumber,
            })
          ).id
        : groupId;
      const { added, total } = await addClocks({
        id: targetId,
        clockIds: devicesIds,
      });
      const alreadyIn = devicesIds.length - added;
      setResult(
        `Added ${added} of ${devicesIds.length} devices${alreadyIn > 0 ? ` (${alreadyIn} were already in the group)` : ""}. The group now holds ${total} clocks.`,
      );
    } catch (e) {
      setError(extractApiErrorMessage(e, "Failed to add devices to the group"));
    }
  };

  const options = [
    ...(groups ?? []).map((group) => ({
      value: group.id,
      label: `${group.name} — v${group.version} (${group.memberCount} clocks)`,
    })),
    { value: CREATE_NEW, label: "+ Create a new group" },
  ];

  return (
    <DSDialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DSDialogTitle>Add to beta group</DSDialogTitle>
      <DSDialogContent>
        {result ? (
          <DSTypography variant="body2">{result}</DSTypography>
        ) : (
          <DSBox
            sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}
          >
            <DSTypography variant="body2">
              {devicesIds.length} device{devicesIds.length === 1 ? "" : "s"}{" "}
              selected. They will update to max(their normal target version, the
              group version) — nothing is sent to the clocks now.
            </DSTypography>

            {isLoading ? (
              <DSBox sx={{ display: "flex", justifyContent: "center", p: 2 }}>
                <DSCircularProgress size={20} />
              </DSBox>
            ) : (
              <DSBox
                sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}
              >
                <DSTypography variant="caption" color="text.secondary">
                  Group
                </DSTypography>
                <DSEnumSelect
                  size="small"
                  value={groupId}
                  options={options}
                  onChange={setGroupId}
                />
              </DSBox>
            )}

            {isNew && (
              <>
                <DSTextField
                  label="Name"
                  size="small"
                  value={newName}
                  placeholder="e.g. pendulum-v2"
                  onChange={(e) => setNewName(e.target.value)}
                />
                <DSTextField
                  label="Version"
                  type="number"
                  size="small"
                  value={newVersion}
                  placeholder="e.g. 512"
                  onChange={(e) => setNewVersion(e.target.value)}
                />
              </>
            )}

            {error && (
              <DSTypography variant="caption" color="error">
                {error}
              </DSTypography>
            )}
          </DSBox>
        )}
      </DSDialogContent>
      <DSDialogActions sx={{ justifyContent: "flex-end", px: 3, pb: 2 }}>
        {result ? (
          <DSButton variant="contained" size="small" onClick={onClose}>
            Close
          </DSButton>
        ) : (
          <>
            <DSButton size="small" onClick={onClose}>
              Cancel
            </DSButton>
            <DSButton
              variant="contained"
              size="small"
              disabled={!canSubmit}
              onClick={handleSubmit}
            >
              Add
            </DSButton>
          </>
        )}
      </DSDialogActions>
    </DSDialog>
  );
};

export default AddToBetaGroupDialog;
